import React from "react";
import { Box, ButtonBase, CircularProgress, Typography, alpha } from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

const TrackerToggle = ({
  checked = false,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const enabled = Boolean(checked);
  const tone = enabled ? "success" : "error";

  return (
    <ButtonBase
      type='button'
      role='switch'
      aria-label={loading ? "Recalculating tracker" : "Use tracker"}
      aria-checked={enabled}
      aria-busy={loading}
      disabled={disabled || loading}
      onClick={() => {
        if (!disabled && !loading) onChange?.(!enabled);
      }}
      sx={(theme) => ({
        display: "inline-flex",
        gap: 1.25,
        alignSelf: "flex-start",
        flexShrink: 0,
        minHeight: 44,
        px: 1.5,
        py: 1,
        border: "1px solid",
        borderColor: alpha(theme.palette[tone].main, 0.35),
        borderRadius: 2,
        bgcolor: alpha(theme.palette[tone].main, 0.08),
        color: theme.palette[tone][theme.palette.mode === "dark" ? "light" : "dark"],
        transition: "background-color 180ms ease, border-color 180ms ease",
        "&:hover": {
          bgcolor: alpha(theme.palette[tone].main, 0.15),
          borderColor: theme.palette[tone].main,
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(theme.palette[tone].main, 0.5)}`,
          outlineOffset: 3,
        },
        "&.Mui-disabled": { opacity: loading ? 0.85 : 0.5 },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      })}
    >
      {loading ? (
        <CircularProgress
          size={20}
          thickness={5}
          color='inherit'
          aria-label='Recalculating'
        />
      ) : (
        <PowerSettingsNewIcon sx={{ fontSize: 20 }} />
      )}
      <Typography
        component='span'
        variant='body2'
        fontWeight={600}
        aria-live='polite'
      >
        {loading ? "Recalculating…" : "Use tracker"}
      </Typography>
      <Typography
        component='span'
        variant='caption'
        aria-hidden='true'
        sx={{ fontWeight: 700, letterSpacing: "0.04em", minWidth: 26 }}
      >
        {enabled ? "ON" : "OFF"}
      </Typography>
      <Box
        component='span'
        aria-hidden='true'
        sx={{
          display: "inline-flex",
          alignItems: "center",
          width: 36,
          height: 22,
          borderRadius: 11,
          bgcolor: `${tone}.main`,
          flexShrink: 0,
        }}
      >
        <Box
          component='span'
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            bgcolor: "common.white",
            boxShadow: 1,
            transform: `translateX(${enabled ? 17 : 3}px)`,
            transition: "transform 180ms ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      </Box>
    </ButtonBase>
  );
};

export default TrackerToggle;
